import {
    createAsyncThunk,
    createSlice,
    isAnyOf,
    PayloadAction,
} from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { AppState } from '../../reducers/root.reducer';
import { RequestServiceResponse } from '@tcc-online/request-service';
import UploadState from '../../types/UploadState';
import Admin360GroupService from '../services/Admin360GroupService';
import Group from '../types/project/360/Group';

const NAME = 'Admin360Groups';

const getGroups = createAsyncThunk(
    `${NAME}/getGroups`,
    Admin360GroupService.getGroups,
);
const getGroup = createAsyncThunk(
    `${NAME}/getGroup`,
    Admin360GroupService.getGroup,
);
const createGroup = createAsyncThunk(
    `${NAME}/createGroup`,
    Admin360GroupService.createGroup,
);
const editGroup = createAsyncThunk(
    `${NAME}/editGroup`,
    Admin360GroupService.editGroup,
);
const deleteGroup = createAsyncThunk(
    `${NAME}/deleteGroup`,
    Admin360GroupService.deleteGroup,
);

export interface Admin360GroupState {
    list: UploadState<Group[]>;
}

const initialState: Admin360GroupState = {
    list: {
        data: [],
        loading: false,
        uploading: false,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder): void => {
        builder.addCase(
            getGroups.pending,
            (state: Admin360GroupState): void => {
                state.list.loading = true;
            },
        );
        builder.addCase(
            getGroups.fulfilled,
            (
                state: Admin360GroupState,
                action: PayloadAction<RequestServiceResponse<Group[]>>,
            ): void => {
                state.list.data = action.payload.data;
            },
        );

        builder.addCase(
            createGroup.pending,
            (state: Admin360GroupState): void => {
                state.list.loading = true;
            },
        );
        builder.addCase(
            createGroup.fulfilled,
            (
                state: Admin360GroupState,
                action: PayloadAction<RequestServiceResponse<Group>>,
            ): void => {
                if (state.list.data?.length >= 0) {
                    state.list.data.push(action.payload.data);
                }
            },
        );

        builder.addCase(
            editGroup.fulfilled,
            (
                state: Admin360GroupState,
                action: PayloadAction<RequestServiceResponse<Group>>,
            ): void => {
                if (!state.list.data) {
                    return;
                }

                const index: number = state.list.data.findIndex(
                    (group: Group) => group.id === action.payload.data?.id,
                );
                if (index >= 0) {
                    state.list.data[index] = action.payload.data;
                }
            },
        );

        builder.addCase(
            deleteGroup.fulfilled,
            (state: Admin360GroupState, action): void => {
                if (!state.list.data) {
                    return;
                }

                const index: number = state.list.data.findIndex(
                    (group: Group) => group.id === action.meta.arg.groupId,
                );
                if (index >= 0) {
                    state.list.data.splice(index, 1);
                }
            },
        );

        builder.addMatcher(
            isAnyOf(getGroups.pending, getGroup.pending),
            (state: Admin360GroupState): void => {
                state.list.loading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getGroups.fulfilled,
                getGroups.rejected,
                getGroup.fulfilled,
                getGroup.rejected,
            ),
            (state: Admin360GroupState): void => {
                state.list.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                createGroup.pending,
                editGroup.pending,
                deleteGroup.pending,
            ),
            (state: Admin360GroupState): void => {
                state.list.uploading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                createGroup.fulfilled,
                createGroup.rejected,
                editGroup.fulfilled,
                editGroup.rejected,
                deleteGroup.fulfilled,
                deleteGroup.rejected,
            ),
            (state: Admin360GroupState): void => {
                state.list.uploading = false;
            },
        );
    },
});

const state = (state: AppState): Admin360GroupState =>
    state.admin['360'].groups;

const Admin360GroupSlice = {
    reducer: slice.reducer,
    actions: {
        getGroups,
        getGroup,
        createGroup,
        editGroup,
        deleteGroup,
    },
    state: {
        all: createSelector(state, (state: Admin360GroupState) => state),
        groups: createSelector(
            state,
            (state: Admin360GroupState) => state.list,
        ),
    },
};

export default Admin360GroupSlice;
